import { isEmpty } from "lodash-es";
import { Alert, Button, DeleteIcon, Space } from "melco-ui";
import { useTranslation } from "react-i18next";
import { Key } from "antd/lib/table/interface";
import { useDeleteBlanks } from "../../../hooks/libraries/blanks/useDeleteBlanks";
import {
  SharedserviceMelcoFusionModelsUserBlankListItem,
  useConfirm,
} from "melco-shared-logic";
import { useShowWarningForRowKeys } from "../../../hooks/table/useShowWarningForRowKeys";

type BulkActionDeleteBlanksProps<T> = {
  selectedRowKeys: Key[];
  setSelectedRowKeys: (keys: Key[]) => void;
  refetch?: () => Promise<any>;
  data?: T[];
};

export const BulkActionDeleteBlanks: React.FC<
  BulkActionDeleteBlanksProps<SharedserviceMelcoFusionModelsUserBlankListItem>
> = ({ selectedRowKeys, setSelectedRowKeys, refetch, data }) => {
  const { t } = useTranslation();

  const deleteBlanks = useDeleteBlanks();
  const showWarningForRowKeys = useShowWarningForRowKeys(selectedRowKeys, data);

  const confirmDelete = useConfirm(
    async () => {
      const successfullyDeletedIds = await deleteBlanks(
        selectedRowKeys.map((key) => key.toString())
      );

      if (refetch) {
        refetch();
      }

      // keep items that were not deleted selected
      setSelectedRowKeys(
        selectedRowKeys.filter(
          (id) => !successfullyDeletedIds.includes(id.toString())
        )
      );
    },
    {
      translationPrefix: "blanks.actions.delete",
      translationOptions: {
        count: selectedRowKeys.length,
      },
      content: showWarningForRowKeys.length && (
        <Space direction="vertical" size="small">
          {t("blanks.actions.delete.confirmation.message", {
            count: selectedRowKeys.length,
          })}
          <Alert
            type="warning"
            withBackground
            description={t("blanks.actions.delete.confirmation.warning", {
              count: showWarningForRowKeys.length,
            })}
            showIcon={false}
          />
        </Space>
      ),
    }
  );

  if (isEmpty(selectedRowKeys)) {
    return null;
  }

  return (
    <Button
      key="bulk-delete"
      icon={<DeleteIcon />}
      onClick={() => {
        confirmDelete();
      }}
    >
      {t("blanks.actions.delete.button")}
    </Button>
  );
};
